<template>
  <div class="bg-theme-card rounded-2xl border border-theme-border shadow-sm overflow-hidden">
    <!-- Tab bar -->
    <div class="flex border-b border-theme-border bg-theme-bg2/50">
      <button v-for="tab in tabs" :key="tab.key"
        @click="activeTab = tab.key"
        class="relative flex-1 py-3.5 text-sm font-semibold transition-colors text-center"
        :class="activeTab === tab.key
          ? 'text-primary-600'
          : 'text-theme-text2 hover:text-theme-text'">
        {{ tab.label }}
        <span v-if="tab.key === 'reviews' && reviewCount > 0"
          class="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold"
          :class="activeTab === 'reviews'
            ? 'bg-primary-600 text-white'
            : 'bg-theme-bg2 text-theme-muted'">
          {{ reviewCount }}
        </span>
        <!-- Active underline -->
        <span v-if="activeTab === tab.key"
          class="absolute bottom-0 left-2 right-2 h-0.5 bg-primary-600 rounded-t-full"></span>
      </button>
    </div>

    <!-- Tab content -->
    <div class="p-5">

      <!-- DESCRIPTION -->
      <div v-show="activeTab === 'description'">
        <div v-if="equipment?.description" class="rich-text text-sm text-theme-text2 leading-relaxed" v-html="sanitizedDescription"></div>
        <p v-else class="text-sm text-theme-muted italic">No description provided.</p>
      </div>

      <!-- SPECIFICATIONS -->
      <div v-show="activeTab === 'specifications'">
        <div v-if="equipment?.specifications?.length" class="divide-y divide-theme-border">
          <div v-for="(spec, i) in equipment.specifications" :key="i"
            class="flex items-center justify-between py-3 first:pt-0 last:pb-0">
            <span class="text-sm text-theme-muted">{{ spec.name }}</span>
            <span class="text-sm font-semibold text-theme-text">{{ spec.value }}</span>
          </div>
        </div>
        <p v-else class="text-sm text-theme-muted italic">No specifications listed.</p>
      </div>

      <!-- TERMS -->
      <div v-show="activeTab === 'terms'">
        <div v-if="equipment?.terms" class="rich-text text-sm text-theme-text2 leading-relaxed" v-html="sanitizedTerms"></div>
        <p v-else class="text-sm text-theme-muted italic">No terms provided.</p>
      </div>

      <!-- REVIEWS -->
      <div v-show="activeTab === 'reviews'">
        <!-- Reviews list -->
        <div v-if="equipment?.equipment_reviews?.length" class="space-y-4 mb-6">
          <div v-for="(review, i) in equipment.equipment_reviews" :key="i"
            class="bg-theme-bg2 rounded-xl p-4">
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center gap-2">
                <div class="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-600/20 flex items-center justify-center text-xs font-bold text-primary-600">
                  {{ (review.user_display || review.user || '?')[0].toUpperCase() }}
                </div>
                <div>
                  <p class="text-sm font-semibold text-theme-text">{{ review.user_display || review.user }}</p>
                  <p class="text-[11px] text-theme-muted">{{ formatDate(review.date_created) }}</p>
                </div>
              </div>
              <div class="flex items-center gap-0.5">
                <template v-for="star in 5" :key="star">
                  <i :class="[
                    'pi text-xs',
                    star <= (review.rating || 0) ? 'pi-star-fill text-yellow-400' : 'pi-star text-neutral-300'
                  ]"></i>
                </template>
              </div>
            </div>
            <p v-if="review.review_text" class="text-sm text-theme-text2 leading-relaxed">
              {{ review.review_text }}
            </p>
          </div>
        </div>
        <div v-else class="text-center py-6 mb-6">
          <i class="pi pi-comments text-3xl text-theme-muted/40 mb-2"></i>
          <p class="text-sm text-theme-muted">No reviews yet. Be the first to review!</p>
        </div>

        <!-- Add review form -->
        <div class="border-t border-theme-border pt-5">
          <h4 class="text-sm font-bold text-theme-text mb-3">Write a Review</h4>
          <form @submit.prevent="submitReview" class="space-y-3">
            <!-- Star rating -->
            <div>
              <label class="block text-xs font-semibold text-theme-text2 mb-1.5">Your Rating</label>
              <div class="flex items-center gap-1">
                <template v-for="star in 5" :key="star">
                  <button type="button" @click="newReview.rating = star"
                    class="transition-transform hover:scale-110 focus:outline-none">
                    <i :class="[
                      'pi text-xl',
                      star <= newReview.rating ? 'pi-star-fill text-yellow-400' : 'pi-star text-neutral-300 hover:text-yellow-300'
                    ]"></i>
                  </button>
                </template>
              </div>
            </div>
            <!-- Text -->
            <div>
              <label class="block text-xs font-semibold text-theme-text2 mb-1.5">Your Review</label>
              <textarea v-model="newReview.text" rows="3"
                class="w-full p-3 bg-theme-bg2 border border-theme-border rounded-xl text-sm text-theme-text focus:outline-none focus:ring-2 focus:ring-primary-600/30 focus:border-primary-600 transition resize-none"
                placeholder="Share your experience..."></textarea>
            </div>
            <!-- Submit -->
            <div class="flex justify-end">
              <button type="submit"
                class="px-5 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-500 text-white text-sm font-semibold transition active:scale-[0.98]">
                Submit Review
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { format } from 'date-fns'
import axios from 'axios'
import DOMPurify from 'dompurify'
import { useAuthStore } from '@/store/auth'
import useNotifications from '@/store/notification'

const props = defineProps({
  equipment: { type: Object, default: () => ({}) },
})

const authStore = useAuthStore()
const { showNotification } = useNotifications()
const api_base_url = import.meta.env.VITE_API_BASE_URL

const activeTab = ref('description')
const tabs = [
  { key: 'description', label: 'Description' },
  { key: 'specifications', label: 'Specs' },
  { key: 'terms', label: 'Terms' },
  { key: 'reviews', label: 'Reviews' },
]

const reviewCount = computed(() => props.equipment?.equipment_reviews?.length || 0)

const sanitizedDescription = computed(() =>
  props.equipment?.description ? DOMPurify.sanitize(props.equipment.description) : ''
)
const sanitizedTerms = computed(() =>
  props.equipment?.terms ? DOMPurify.sanitize(props.equipment.terms) : ''
)

const newReview = ref({ rating: null, text: '' })

const formatDate = (date) => {
  try { return format(new Date(date), 'MMM d, yyyy') }
  catch { return 'Invalid Date' }
}

const submitReview = async () => {
  if (!authStore.isAuthenticated) {
    showNotification('Authentication Required', 'Log in to submit a review.', 'error')
    return
  }
  if (!props.equipment?.id) {
    showNotification('Error', 'Item data unavailable.', 'error')
    return
  }
  const reviewData = {
    rating: newReview.value.rating,
    review_text: newReview.value.text.trim(),
    equipment: props.equipment.id,
  }
  try {
    const response = await axios.post(`${api_base_url}/api/reviews/`, reviewData, {
      withCredentials: true, timeout: 10000,
    })
    if (!Array.isArray(props.equipment.equipment_reviews)) {
      props.equipment.equipment_reviews = []
    }
    if (response.data && (response.data.id || response.data.review_text)) {
      props.equipment.equipment_reviews.push(response.data)
      showNotification('Success', 'Thank you for your review!', 'success')
    } else {
      showNotification('Success', 'Thank you for your rating!', 'success')
    }
    newReview.value = { rating: null, text: '' }
  } catch (error) {
    if (error.code === 'ECONNABORTED') {
      showNotification('Timeout', 'Request timed out. Try again.', 'error')
    } else if (error.response) {
      const { status, data } = error.response
      if (status === 403 && data?.error) showNotification('Denied', data.error, 'error')
      else if (status >= 500) showNotification('Server Error', 'Try again later.', 'error')
      else showNotification('Error', data?.message || 'Failed to submit review.', 'error')
    } else {
      showNotification('Error', 'Something went wrong.', 'error')
    }
  }
}
</script>

<style scoped>
.rich-text :deep(ol) {
  list-style-type: decimal;
  padding-left: 1.5em;
  margin: 0.5em 0;
}
.rich-text :deep(ul) {
  list-style-type: disc;
  padding-left: 1.5em;
  margin: 0.5em 0;
}
.rich-text :deep(li) {
  margin: 0.25em 0;
}
.rich-text :deep(strong) {
  font-weight: 700;
}
.rich-text :deep(em) {
  font-style: italic;
}
.rich-text :deep(u) {
  text-decoration: underline;
}
.rich-text :deep(s) {
  text-decoration: line-through;
}
.rich-text :deep(p) {
  margin: 0.4em 0;
}
.rich-text :deep(h1) {
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0.6em 0 0.3em;
}
.rich-text :deep(h2) {
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0.5em 0 0.25em;
}
.rich-text :deep(h3) {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0.4em 0 0.2em;
}
.rich-text :deep(blockquote) {
  border-left: 3px solid var(--color-primary-600, #e68d00);
  padding-left: 0.75rem;
  margin: 0.5em 0;
  color: var(--color-text2, #6b7280);
}
.rich-text :deep(a) {
  color: var(--color-primary-600, #e68d00);
  text-decoration: underline;
}
.rich-text :deep(.ql-indent-1) {
  padding-left: 1.5em;
}
.rich-text :deep(.ql-indent-2) {
  padding-left: 3em;
}
.rich-text :deep(.ql-indent-3) {
  padding-left: 4.5em;
}
.rich-text :deep(.ql-align-center) {
  text-align: center;
}
.rich-text :deep(.ql-align-right) {
  text-align: right;
}
.rich-text :deep(.ql-align-justify) {
  text-align: justify;
}
</style>
